"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

type Testimonial = {
  body: string;
  author: {
    name: string;
    handle: string;
    organization: string;
    imageUrl: string;
  };
};

export default function TestimonialsSectionCarousel() {
  const swiperRef = useRef<SwiperType | null>(null);

  const testimonials: Testimonial[] = [
    {
      body: "Using Accelerator tools helped my PhD student have the most efficient and productive summer internship I’ve been a part of so far. We already have the key results of the paper ready, and June just ended. Had we done this with other Princeton infrastructure, it would have been much harder. Given the pace and quality of the work so far, we’re optimistic that this will lead to a strong paper submission, potentially to a top conference.",
      author: {
        name: "Manoel Horta",
        handle: "Assistant Professor",
        organization: "Princeton University",
        imageUrl:
          "https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
      },
    },
    {
      body: "The Databricks environment and Spark make it easy to work with large scale social media data. Where previously I'd spend days to weeks gathering data, setting up storage, and writing/debugging complicated, parallelized code I can now just log in, write a simple Python script, and Databricks handles the rest. Large scale data feels like laptop scale data on this platform.",
      author: {
        name: "Sean Norton",
        handle: "Research Scientist",
        organization: "Princeton University",
        imageUrl:
          "https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
      },
    },
    {
      body: `I used Princeton SMART in a research project on online job searching to examine how individuals from different socioeconomic and racial/ethnic backgrounds navigate employment information online and what this means for social mobility.

Princeton SMART’s automated OCR and app log capture saved me months of work by providing consistent, ready-to-analyze data. Without these features, I would have had to wait until the end of data collection and then set up a separate cloud computing pipeline for OCR, which would delay the analysis and incur additional computing costs.

The combination of automated OCR-ed text and detailed app logs has been the most valuable. These features allow for cross-platform measurement of online information searches, making the data collection broader and more comprehensive rather than limited to a single app or platform.

For an upcoming analysis, we’re using the text-extracted data to efficiently identify employers shown on participants’ screens and cross-check them against government and media records of reported job scams - a task that would otherwise require extensive manual tracking and verification.`,
      author: {
        name: "Jihye Lee",
        handle: "Assistant-Professor",
        organization:
          "School of Advertising & PR, The University of Texas at Austin",
        imageUrl:
          "https://images.unsplash.com/photo-1603415526960-f7e0328d13cf?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
      },
    },
  ];

  return (
    <div
      id="testimonials"
      className="relative bg-white from-bgHome via-[#172035] to-bgHome py-24 dark:bg-gradient-to-r sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base/7 font-semibold text-[#348ddb]">
            Testimonials
          </h2>
          <p className="mt-2 text-balance text-4xl font-semibold tracking-tight text-[#002243] dark:text-white">
            What Researchers Are Saying
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-[#002243] dark:text-white">
            Explore feedback from our beta testers and dive into real-world use
            cases.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative mx-auto mt-16 max-w-7xl sm:mt-20">
          {/* Navigation buttons */}
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className="absolute left-0 top-1/2 z-10 grid h-10 w-10 -translate-x-4 -translate-y-1/2 place-items-center rounded-full border border-gray-200 bg-white shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="h-5 w-5 text-[#348ddb]" />
          </button>
          <button
            onClick={() => swiperRef.current?.slideNext()}
            className="absolute right-0 top-1/2 z-10 grid h-10 w-10 -translate-y-1/2 translate-x-4 place-items-center rounded-full border border-gray-200 bg-white shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:bg-white/10 dark:hover:bg-white/20"
            aria-label="Next"
          >
            <ChevronRightIcon className="h-5 w-5 text-[#348ddb]" />
          </button>

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Pagination]}
            spaceBetween={24}
            slidesPerView={1}
            loop
            pagination={{ clickable: true }}
            className={`!pb-12
              [--swiper-pagination-bullet-inactive-color:#d1d5db]
              [--swiper-pagination-bullet-inactive-opacity:1]
              [--swiper-pagination-bullet-size:8px]
              [--swiper-pagination-color:#348ddb]
              dark:[--swiper-pagination-bullet-inactive-color:#475569]
              dark:[--swiper-pagination-color:#60a5fa]
            `}
          >
            {testimonials.map((t) => (
              <SwiperSlide key={t.author.handle}>
                <figure className="mx-auto w-full max-w-3xl rounded-2xl border border-gray-200 bg-white/80 p-6 text-sm/6 shadow-md dark:border-white/10 dark:bg-white/5 dark:backdrop-blur sm:p-8 lg:max-w-4xl">
                  <blockquote className="text-gray-900 dark:text-gray-200">
                    <p className="text-[15px] leading-7 sm:text-base sm:leading-8">{`"${t.body}"`}</p>
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-x-4">
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-gray-200">
                        {t.author.name}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">
                        {t.author.handle}
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">
                        {t.author.organization}
                      </div>
                      <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
                    </div>
                  </figcaption>
                </figure>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
